
import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { Send, MessageSquare, Loader2, CheckCircle2, Lightbulb, AlertCircle } from 'lucide-react';

interface Props {
  userEmail: string | undefined;
}

export const Feedback: React.FC<Props> = ({ userEmail }) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !userEmail) return;

    setIsSending(true);
    setErrorMsg(null);

    try {
      // 1. Salva no Supabase (Banco de Dados) - Fonte da verdade principal
      const { error: dbError } = await supabase
        .from('feedbacks')
        .insert([
          { 
            user_email: userEmail, 
            message: message.trim(),
            created_at: new Date().toISOString()
          }
        ]);

      if (dbError) {
          console.error('Error saving feedback to DB:', dbError);
          // Se falhar o banco, mas não for erro de tabela inexistente, interrompe.
          // Se for erro de tabela (42P01 - comum em dev), tentamos enviar o email mesmo assim.
          if (dbError.code !== '42P01') { 
             throw new Error("Erro ao salvar feedback no banco de dados."); 
          }
      }

      // 2. Dispara a Edge Function para enviar o e-mail via Resend (Opcional/Secundário)
      // Envolvemos em try/catch silencioso para não bloquear a UI de sucesso se o backend não estiver deployado
      try {
          const { error: functionError } = await supabase.functions.invoke('send-feedback', {
            body: {
              message: message.trim(),
              userEmail: userEmail
            }
          });

          if (functionError) {
            console.warn('Aviso: Falha ao invocar função de email (feedback salvo apenas no banco):', functionError);
          }
      } catch (edgeError) {
          // Ignora erro de rede/deploy da função para não frustrar o usuário
          console.warn('Edge function not reachable:', edgeError);
      }

      // 3. Sucesso (Se chegou aqui, pelo menos salvou no banco ou tentou)
      setSuccess(true);
      setMessage('');
      setTimeout(() => setSuccess(false), 3000);

    } catch (err: any) {
      setErrorMsg(err.message || 'Não foi possível enviar sua sugestão. Tente novamente.');
      console.error(err);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in pb-20">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-yellow-600 p-2 rounded-lg">
             <Lightbulb className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white">Sugestões e Melhorias</h2>
      </div>
      
      <p className="text-slate-400">
        Sua opinião é fundamental! Encontrou um erro? Tem uma ideia para uma nova funcionalidade? 
        Escreva abaixo e ajude a construir o Driver Score.
      </p>

      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 shadow-lg relative overflow-hidden">
        
        {success && (
            <div className="absolute inset-0 bg-slate-800/90 backdrop-blur-sm flex flex-col items-center justify-center z-10 animate-fade-in">
                <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
                <h3 className="text-xl font-bold text-white">Obrigado!</h3>
                <p className="text-slate-400">Sua sugestão foi enviada com sucesso.</p>
            </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                    O que podemos melhorar?
                </label>
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    rows={6}
                    placeholder="Ex: Gostaria de poder exportar meu histórico em PDF..."
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg p-4 text-white placeholder-slate-500 focus:ring-2 focus:ring-yellow-500 outline-none resize-none transition-all"
                />
            </div>

            {errorMsg && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 p-3 rounded-lg border border-red-900/50">
                    <AlertCircle className="w-4 h-4" />
                    {errorMsg}
                </div>
            )}

            <div className="flex justify-end">
                <button
                    type="submit"
                    disabled={isSending || !message.trim()}
                    className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg flex items-center gap-2 transition-all shadow-lg hover:shadow-yellow-900/20"
                >
                    {isSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    Enviar Sugestão
                </button>
            </div>
        </form>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-blue-400 mt-1" />
              <div>
                  <h4 className="font-bold text-white text-sm">Transparência</h4>
                  <p className="text-xs text-slate-400 mt-1">Lemos todas as mensagens enviadas por aqui para priorizar as atualizações.</p>
              </div>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400 mt-1" />
              <div>
                  <h4 className="font-bold text-white text-sm">Evolução Constante</h4>
                  <p className="text-xs text-slate-400 mt-1">Este app é feito para motoristas, por motoristas. Sua visão molda o futuro.</p>
              </div>
          </div>
      </div>
    </div>
  );
};
